import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { LandingRouteService } from '@core/services/landing-route.service';
import { from, map, Observable, of, switchMap } from 'rxjs';

/**
 * Any page that is not part of onboarding/join: if the user is signed in and has
 * no workspaces, send them to onboarding — but to workspace creation only once the
 * invitation list confirms there is nothing to accept, since zero memberships and
 * a waiting invitation look identical in `myOrganizations`.
 */
export const noWorkspaceRedirectGuard: CanActivateFn = ():
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const landing = inject(LandingRouteService);

  auth.purgeLegacyDemoStorage();

  if (!auth.hasToken()) {
    return true;
  }

  const resolve = (
    user: ReturnType<AuthService['currentUser']>,
  ): Observable<boolean | UrlTree> => {
    if (!user) return of(true);
    if (user.platformRole === 'super_admin') return of(true);
    if (user.workspaces.length > 0) return of(true);

    return from(landing.pendingInvitationCount()).pipe(
      // A failed lookup returns INVITATIONS_UNKNOWN, which lands on the hub too —
      // only a confirmed empty list may route to creation.
      map((count) =>
        router.parseUrl(count === 0 ? '/workspace-creation' : '/select-workspace'),
      ),
    );
  };

  const existing = auth.currentUser();
  if (existing) return resolve(existing);

  return auth.ensureSession().pipe(switchMap(resolve));
};
